import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User, LogOut, Bell } from 'lucide-react';
import { debug } from '@/lib/utils';

// Create a context to manage header state across components
type HeaderContextType = {
  isCompact: boolean;
  setCompact: (value: boolean) => void;
  setExpanded: () => void;
  isLoggedIn: boolean;
};

const HeaderContext = createContext<HeaderContextType>({
  isCompact: false,
  setCompact: () => {},
  setExpanded: () => {},
  isLoggedIn: false,
});

export const useHeaderState = () => useContext(HeaderContext);

// Get a random but consistent image for the day
function getImageForToday() {
  const today = new Date();
  const dateKey = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  // Simple hash of the date to randomly but consistently select an image
  return dateKey % 2 === 0
    ? "/tradespurple/tradespurple-illustration.png"
    : "/tradespurple/TPman.png";
}

type TradesPurpleHeaderProviderProps = {
  children: ReactNode;
};

export function TradesPurpleHeaderProvider({ children }: TradesPurpleHeaderProviderProps) {
  const [isCompact, setIsCompact] = useState(false);
  const { isAuthenticated } = useAuth();

  const setCompact = (value: boolean) => {
    setIsCompact(value);
  };

  const setExpanded = () => {
    setIsCompact(false);
  };

  return (
    <HeaderContext.Provider value={{ isCompact, setCompact, setExpanded, isLoggedIn: isAuthenticated }}>
      {children}
    </HeaderContext.Provider>
  );
}

type TradesPurpleHeaderProps = {
  logoOnly?: boolean;
  forceCompact?: boolean;
};

export function TradesPurpleHeader({ logoOnly = false, forceCompact = false }: TradesPurpleHeaderProps) {
  const { isCompact, setCompact } = useHeaderState();
  const { isAuthenticated, user, signOut } = useAuth();
  const location = useLocation();
  const shouldBeCompact = forceCompact || isCompact;

  // Set up intersection observer to detect scrolling
  useEffect(() => {
    if (logoOnly || forceCompact) return; // Don't track scroll for logo-only mode or when compact is forced

    // Create a sentinel element at the top of the page
    const sentinel = document.createElement('div');
    sentinel.style.position = 'absolute';
    sentinel.style.top = '0';
    sentinel.style.height = '1px';
    sentinel.style.width = '100%';
    document.body.appendChild(sentinel);

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is out of view (scrolled down), make header compact
        setCompact(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-60px 0px 0px 0px' } // Trigger after scrolling ~60px
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      document.body.removeChild(sentinel);
    };
  }, [logoOnly, forceCompact, setCompact]);

  // Check if user is on their account page
  const isAccountPage = isAuthenticated && location.pathname === '/';

  // Get image path based on today's date (will be stable for the whole day)
  const imageSrc = getImageForToday();
  const imageAlt = imageSrc.includes("TPman")
    ? "TradesPurple illustration showing a man using the service on his phone"
    : "TradesPurple illustration showing a woman using the service on her phone";

  const handleLogout = async () => {
    try {
      await signOut();
      // No need to navigate, AuthContext will handle that
    } catch (error) {
      debug.error('Logout failed:', error);
    }
  };

  // If logoOnly is true, render compact version without login/logout links
  if (logoOnly) {
    return (
      <div className="flex items-center py-2 justify-center">
        <h1 className="text-3xl font-bold text-purple-800 mr-2">TradesPurple</h1>
      </div>
    );
  }

  // For logged-in users, always use the compact header
  if (isAuthenticated && forceCompact) {
    return (
      <div className="sticky top-0 z-50 bg-slate-50 shadow-sm pt-3 pb-2 safe-inset-top">
        <div className="flex items-center justify-between px-3">
          <div className="flex items-center">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="h-12 w-12 mr-3 object-cover rounded-full"
            />
            <h1 className="text-2xl font-bold text-purple-800">TradesPurple</h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-3 py-1.5 rounded-md bg-slate-100 text-purple-700 hover:bg-slate-200"
          >
            <LogOut className="h-4 w-4 mr-1.5" />
            <span className="text-sm font-medium hidden sm:inline">Log out</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`sticky top-0 z-50 bg-slate-50 transition-all duration-300 ease-in-out safe-inset-top ${
        shouldBeCompact ? 'pt-2 pb-1 shadow-sm' : 'pt-3 pb-2 mb-2'
      }`}
    >
      {/* Login/Logout Link */}
      <div className="absolute top-3 right-3 md:right-6 z-10">
        {isAuthenticated ? (
          isAccountPage ? (
            <button
              onClick={handleLogout}
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <LogOut className="h-5 w-5 mr-1" />
              <span className="text-sm">Log out</span>
            </button>
          ) : (
            <Link
              to="/"
              className="flex items-center text-purple-800 hover:text-purple-900"
            >
              <User className="h-5 w-5 mr-1" />
              <span className="text-sm">My Page</span>
            </Link>
          )
        ) : (
          <Link
            to="/auth/login"
            className="flex items-center text-purple-800 hover:text-purple-900"
          >
            <User className="h-5 w-5 mr-1" />
            <span className="text-sm">Log in</span>
          </Link>
        )}
      </div>

      {/* Main Header Content */}
      {shouldBeCompact ? (
        // Compact header - Logo and person image on same line
        <div className="flex items-center">
          <div className="flex items-center">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="h-8 w-8 mr-2 object-cover rounded-full"
            />
            <h1 className="text-2xl font-bold text-purple-800">TradesPurple</h1>
          </div>
        </div>
      ) : (
        // Expanded header with stacked elements
        <div className="flex flex-col items-center">
          <div className="flex justify-center w-full">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="h-48 max-w-full transition-all duration-300"
            />
          </div>
          <h1 className="text-2xl font-bold text-purple-800 mt-2">TradesPurple</h1>
        </div>
      )}
    </div>
  );
}
